import { COMPOSE_EMAIL_TOOL } from '@realadvisor/common-types';

import type { ToolPart } from './ApprovalActions';
import { savedArtifactOf, shownFilesOf } from './artifactFiles';
import type { MessagePart } from './ChatMessage';
import type { ChatStep } from './ChatSteps';
import { shownCrmImagesOf } from './crmImages';
import { isVisualToolName, visualToolPhaseOf } from './visualToolOutput';

// Mirror ADD_PROPERTY_IMAGES_TOOL_NAME in AddPropertyImagesToolCard.tsx and
// ASK_CLARIFYING_QUESTIONS_TOOL in @realadvisor/common-types. Kept as local
// literals so this module stays importable from node tests.
const ADD_PROPERTY_IMAGES_TOOL = 'add_property_images';
const ASK_CLARIFYING_QUESTIONS_TOOL = 'ask_clarifying_questions';
const STANDALONE_CARD_TOOLS = new Set([
  ADD_PROPERTY_IMAGES_TOOL,
  ASK_CLARIFYING_QUESTIONS_TOOL,
]);

const isStandaloneCard = (toolPart: ToolPart): boolean =>
  STANDALONE_CARD_TOOLS.has(toolPart.toolName) ||
  (isVisualToolName(toolPart.toolName) &&
    visualToolPhaseOf(toolPart) === 'done') ||
  savedArtifactOf(toolPart) != null ||
  shownFilesOf(toolPart) != null ||
  shownCrmImagesOf(toolPart) != null ||
  (toolPart.toolName === COMPOSE_EMAIL_TOOL &&
    (toolPart.state == null ||
      toolPart.state === 'input-streaming' ||
      toolPart.state === 'input-available'));

export type MessageSegment =
  | { kind: 'text'; key: string; text: string }
  | { kind: 'file'; key: string; part: MessagePart }
  | { kind: 'card'; key: string; part: ToolPart }
  | { kind: 'steps'; key: string; steps: Array<ChatStep> };

const isToolPart = (type: string): boolean =>
  type.startsWith('tool-') || type === 'dynamic-tool';

const toolNameOf = (part: MessagePart): string =>
  part.type === 'dynamic-tool'
    ? part.toolName ?? 'tool'
    : part.type.slice('tool-'.length);

const toToolPart = (part: MessagePart): ToolPart => ({
  toolName: toolNameOf(part),
  toolCallId: part.toolCallId,
  state: part.state,
  input: part.input,
  output: part.output,
  errorText: part.errorText,
  approval: part.approval,
});

// Folds runs of consecutive reasoning + tool parts into one "steps" segment
// (rendered by ChatSteps). Text and file parts end a run, and so do the
// add_property_images card (thumbnail approval UI) and the
// ask_clarifying_questions card, which stay standalone.
// The compose_email card (the inline email composer) is standalone only until
// it is answered; then it is a step and joins the runs around it. A visual
// (render_visual / generate_image) is the other way round: a step while it
// runs or when it fails (the agent usually retries), a standalone card once
// it has an image, since the image is the answer itself. save_artifact works
// the same way: a step until it has saved a file, then the file card, and so
// do show_files and show_crm_images (a step until they have something to
// show, then the card).
// Empty text/reasoning parts and markers such as `step-start` are skipped
// without ending the run. Keys use the index of the segment's first part,
// which stays stable as parts stream in.
export const groupParts = (
  parts: ReadonlyArray<MessagePart>,
): Array<MessageSegment> => {
  const segments: Array<MessageSegment> = [];
  let run: Extract<MessageSegment, { kind: 'steps' }> | null = null;
  const pushStep = (index: number, step: ChatStep) => {
    if (run == null) {
      run = { kind: 'steps', key: `steps-${index}`, steps: [] };
      segments.push(run);
    }
    run.steps.push(step);
  };
  parts.forEach((part, index) => {
    const text = part.text ?? '';
    if (part.type === 'text') {
      if (text.length > 0) {
        run = null;
        segments.push({ kind: 'text', key: `text-${index}`, text });
      }
    } else if (part.type === 'reasoning') {
      if (text.length > 0) {
        pushStep(index, { kind: 'reasoning', text });
      }
    } else if (part.type === 'file') {
      run = null;
      segments.push({ kind: 'file', key: `file-${index}`, part });
    } else if (isToolPart(part.type)) {
      const toolPart = toToolPart(part);
      if (isStandaloneCard(toolPart)) {
        run = null;
        segments.push({ kind: 'card', key: `tool-${index}`, part: toolPart });
      } else {
        pushStep(index, { kind: 'tool', part: toolPart });
      }
    }
  });
  return segments;
};

// The first ask_clarifying_questions call in a turn still waiting for the
// user. It is answered from the card docked above the chat input; parallel
// calls are answered one after the other.
export const findPendingClarifyPart = (
  parts: ReadonlyArray<MessagePart>,
): ToolPart | null => {
  for (const part of parts) {
    if (!isToolPart(part.type)) {
      continue;
    }
    const toolPart = toToolPart(part);
    if (
      toolPart.toolName === ASK_CLARIFYING_QUESTIONS_TOOL &&
      toolPart.state === 'input-available' &&
      toolPart.output == null &&
      toolPart.errorText == null
    ) {
      return toolPart;
    }
  }
  return null;
};

// A compose_email call still waiting for the user to send or discard it. Like
// a pending approval, the turn is still in flight while it is open.
export const isPendingComposeEmailPart = (part: MessagePart): boolean =>
  isToolPart(part.type) &&
  toolNameOf(part) === COMPOSE_EMAIL_TOOL &&
  part.state === 'input-available';
